import { useMemo } from "react";
import { Stack, useTheme } from "@mui/material";
import { FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { FacetFilterPanel, type FacetSelection } from "src/components/FacetFilters";
import {
    adaptFacetSelectionChange,
    toFacetDimensions,
    useAuditLogFacetLabels,
    type AuditLogFacetSection,
} from "./useAuditLogFacetSections";

export const AUDIT_LOG_RAIL_WIDTH = 40;

type AuditLogFacetRailProps = {
    sections: AuditLogFacetSection[];
    onSectionChange: (section: AuditLogFacetSection, selection: FacetSelection) => void;
    isCollapsed: boolean;
    onCollapsedChange: (isCollapsed: boolean) => void;
};

export const AuditLogFacetRail = (props: AuditLogFacetRailProps) => {
    const { sections, onSectionChange, isCollapsed, onCollapsedChange } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const labels = useAuditLogFacetLabels();
    const dimensions = useMemo(() => toFacetDimensions(sections), [sections]);
    const handleSelectionChange = useMemo(
        () => adaptFacetSelectionChange(sections, onSectionChange),
        [sections, onSectionChange],
    );

    if (isCollapsed) {
        return (
            <Stack flexShrink={0} paddingTop={theme.spacing(0.5)}>
                <FoodsIconButton
                    icon="filter"
                    size="s"
                    tooltip={{ text: t("settings.audit_log.facet.expand", "Show the facets") }}
                    buttonProps={{ onClick: () => onCollapsedChange(false) }}
                />
            </Stack>
        );
    }

    return (
        <Stack
            gap={theme.spacing(2)}
            width={theme.spacing(AUDIT_LOG_RAIL_WIDTH)}
            flexShrink={0}
            padding={theme.spacing(2)}
            sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.spacing(1),
                alignSelf: "flex-start",
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="small-semibold">{t("settings.audit_log.facet.title", "Facets")}</Typography>
                <FoodsIconButton
                    icon="closePanel"
                    size="xs"
                    tooltip={{ text: t("settings.audit_log.facet.collapse", "Hide the facets") }}
                    buttonProps={{ onClick: () => onCollapsedChange(true) }}
                />
            </Stack>
            <FacetFilterPanel dimensions={dimensions} onSelectionChange={handleSelectionChange} labels={labels} />
        </Stack>
    );
};
